import { useRef } from 'react'
import { useGSAP } from '@gsap/react'

/**
 * FrameworksWave - the dot-wave backdrop behind #frameworks ("FRAMEWORKS WE
 * DESIGN TO" + the PHIPA/HIPAA/… chips) on home, GENERATED AND ANIMATED on a
 * canvas rather than panned as a picture.
 *
 * ── What changed, and why the old approach had a ceiling ───────────────────
 * This component used to tween the <img> (/assets/frameworks-wave-tight.webp)
 * with GSAP: xPercent ±1.2 at scale 1.035, a 24s round trip. That was the most
 * a raster backdrop can do, and its own docstring said so - "there is nothing to
 * tween per dot… a per-dot, x-staggered ripple would mean rebuilding that
 * generator at runtime on a canvas". This is that rebuild.
 *
 * The distinction is worth stating precisely, because it is the whole point:
 * panning an image of a wave moves the wave's PICTURE sideways. Generating the
 * surface means the wave itself travels - each dot rises and falls as the phase
 * passes through it, which is what reads as water rather than as wallpaper.
 *
 * ── The surface is the same maths the asset was baked from ─────────────────
 * The note on .frameworks-art in src/StyleOverrides.jsx records the generator:
 * "a sine-wave surface sampled on a 30x200 grid with each sample drawn as a dot,
 * perspective-compressed and faded with depth". Those numbers are reproduced
 * below (ROWS/COLS) so the canvas is recognisably the same artwork in motion,
 * not a different backdrop that happens to move. It is rasterised as WebP in the
 * repo because the SVG form would be 6,000 <circle> elements / ~330 KB of markup
 * - a constraint that does not apply to a canvas, which draws the same 6,000
 * dots with no DOM at all.
 *
 * ── Why fillRect and not arc() ────────────────────────────────────────────
 * 6,000 dots at 60fps is 360,000 draw calls a second, and ctx.arc() + fill() per
 * dot does not survive that on an integrated GPU. Two things make it cheap:
 *   - fillRect for dots ≤ ~2.5px. At this size a square and a circle are
 *     indistinguishable once the scrim is over them, and fillRect is several
 *     times faster.
 *   - ALPHA BUCKETING. fillStyle changes are the expensive part, so the depth
 *     fade is quantised into ALPHA_STEPS levels and dots are drawn bucket by
 *     bucket: ~10 fillStyle assignments per frame instead of 6,000.
 *
 * ── Why it draws UNDER the scrim, as a sibling of the img ──────────────────
 * .frameworks-art::after is the two-layer scrim (a radial centre-hold over a
 * vertical ramp) that holds white copy legible on this band, and ::after paints
 * after every normal child. Inserting the canvas as a normal child therefore
 * puts it under that scrim automatically, which is what lets every contrast
 * number measured for the raster version carry over unchanged.
 *
 * Do NOT touch .frameworks-art itself: it carries transform: translateX(-50%)
 * (measured matrix(1,0,0,1,-720,0) at 1440), which is what breaks the art out of
 * the .shell box to full-bleed 100vw. Writing a transform there would shove the
 * band half a viewport sideways.
 *
 * ── The copy column is protected in the ART, not just by the scrim ─────────
 * A generated field can do something the raster could not: not put brightness
 * where the copy is in the first place. COPY_HOLD dims dots inside the centred
 * copy column (the eyebrow, the six chips and the disclaimer are all centred
 * here). This is the same shape as the scrim's radial centre-hold, applied a
 * layer earlier - which is why the band can be brighter overall than the image
 * was while the copy sits on a quieter field.
 *
 * ── Fallback is the image, and it is deliberate in three cases ────────────
 * The <img> stays in the markup and is hidden by CSS only when a live canvas is
 * present (`.frameworks-art:has(> canvas[data-pb-wave]) > img`). So no JS, no 2D
 * context, or prefers-reduced-motion all fall back to the still artwork, which
 * is the correct result for each - under reduce this component draws ONE frame
 * and stops rather than removing the art.
 *
 * ── Lifecycle is inherited from the panning version and still load-bearing ─
 * The route guard, the IntersectionObserver gate and the MutationObserver
 * re-bind are kept as-is:
 *   - PB_parkPage (Pentabay.html) does node.remove() on navigation and stashes
 *     the node for reuse, so an unguarded rAF loop would keep drawing to a
 *     detached canvas for the rest of the session. IO is layout-driven, so a
 *     detached or hidden target simply stops intersecting - ScrollTrigger would
 *     not notice, because it resolves to scroll offsets once.
 *   - home is re-attached as the SAME node later, so binding is per-element
 *     idempotent (data-pbWave) rather than one-shot.
 */

const ART_SEL = '#frameworks .frameworks-art'
const BUILT = 'pbWave'

/* Grid, from the generator note in StyleOverrides. COLS runs across the band,
   ROWS is depth. 200x30 = 6,000 dots, the same count the asset was baked from. */
const COLS = 200
const ROWS = 30

/* ── THE SURFACE MODEL, and why it is not a perspective floor ──────────────
 * First build used a perspective divide - a ground plane receding to a horizon,
 * the classic dot-floor. Wrong shape, and comparing it against the asset is what
 * showed why: frameworks-wave-tight.webp is not a floor, it is a RIBBON. A sheet
 * of dots seen almost edge-on, spanning the full width, cresting near the top
 * around x≈0.47 and sweeping down to a trough either side of it, with the rows
 * running left-to-right and stacking DOWNWARD as they recede. A perspective
 * divide crushed all thirty rows into a thin band near the horizon line, which
 * rendered as a faint ribbon in the wrong place.
 *
 * So the model is a stack of ROWS copies of the same wave curve, each pushed
 * down and phase-shifted a little. The phase shift is what produces the diagonal
 * texture visible in the asset - without it the rows line up vertically and the
 * sheet reads as a striped grid instead of a surface with a direction. */
const WAVES = 1.6          // crests across the width; matches the asset's ~1.5
const AMPLITUDE = 0.235    // of band height
const BASE = 0.33          // centre line of the TOP row, fraction of height
const SHEET_DEPTH = 0.52   // total downward spread of the stack, fraction of height
const ROW_CROWD = 1.28     // >1 crowds rows toward the top, as the asset does
const DEPTH_PHASE = 0.9    // radians of shift front-to-back; the diagonal

/* TRAVEL is the whole speed control: radians of phase per second. One crest
   crosses the band in 2π/(TRAVEL·2π) ÷ WAVES ≈ 1/(TRAVEL·WAVES) seconds. */
const TRAVEL = 0.13

const DOT_NEAR = 2.2       // px, top row
const DOT_FAR = 1.25       // px, back row
const ALPHA_NEAR = 0.80
const ALPHA_FAR = 0.05
const ALPHA_STEPS = 10     // see the bucketing note above

/* ── THE COPY HOLD, and why it is measured rather than a constant ───────────
 * A generated field can do the thing a picture could not: read where the copy
 * actually is and not put brightness there. The hold zone is the union bounding
 * box of this band's own copy elements, measured from the DOM at build and on
 * every resize and converted to the art box's coordinate space. Dots inside it
 * are dimmed on a smooth elliptical falloff, so it reads as the field passing
 * behind a soft vignette rather than as a hole punched in a rectangle.
 *
 * Self-maintaining on purpose: change the copy, add a chip, re-word the
 * disclaimer, and the hold follows it. The alternative was four magic numbers
 * that silently stop matching the moment the band's content changes.
 *
 * It replaced an x-only version (a centred column mirroring the scrim's radial
 * hold), which could not know that a TRAVELLING wave puts its crest through a
 * given line periodically - a static picture cannot do that, so the raster
 * version never needed to care.
 *
 * Measured white-on-background under each run of copy at 1440, dark theme,
 * raster version -> this canvas:
 *     eyebrow      p99 5.54 -> 7.87     worst 4.73 -> 4.88
 *     chips        p99 10.49 -> 10.04   worst 9.31 -> 7.88
 *     disclaimer   p99 7.93 -> 10.59    worst 6.09 -> 7.27
 * >> If you re-measure, read the MEASUREMENT WARNING on .frameworks-art::after
 * >> in StyleOverrides first. Suppressing the copy with `color: transparent`
 * >> does not stop it painting on this band, and a `visibility` transition means
 * >> `visibility: hidden` needs transitions off and a computed-value assertion
 * >> before the pixels mean anything. Both of those produced a false failure
 * >> once already.
 *
 * HOLD_MIN is how much of the dot's alpha survives dead centre. It multiplies
 * with the scrim, which is still there - this only stops the ART putting its
 * brightest pixels under the text in the first place. */
const HOLD_MIN = 0.10
const HOLD_PAD = 1.22      // grow the measured box a little so the falloff starts
                           // outside the glyphs, not on them

const THEMES = {
  dark:  { r: 128, g: 178, b: 255 },
  light: { r: 22,  g: 52,  b: 104 },
}

export default function FrameworksWave() {
  const cleanups = useRef([])

  useGSAP(() => {
    let observer = null

    const build = () => {
      const route = document.documentElement.getAttribute('data-route')
      if (!route) return
      const page = document.querySelector('main.page.active')
      /* The inline router stamps <html data-route> while the OUTGOING page is
         still main.page.active, so without this the canvas can be built into a
         node that is about to be detached and the real band is never touched. */
      if (!page || page.id !== `page-${route}`) return

      const art = page.querySelector(ART_SEL)
      if (!art || art.dataset[BUILT] === '1') return

      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d', { alpha: true })
      // No context (very old or GPU-blocked): leave the <img> visible and stop.
      if (!ctx) return
      canvas.setAttribute('data-pb-wave', '')
      canvas.setAttribute('aria-hidden', 'true')

      art.dataset[BUILT] = '1'
      // insertBefore the img rather than append: paint order among siblings is
      // document order, and ::after covers both either way, but this keeps the
      // canvas under the img if the hiding rule ever fails to match.
      art.insertBefore(canvas, art.firstChild)

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')
      let W = 0, H = 0, dpr = 1
      let raf = 0
      let phase = 0
      let last = 0

      const themeColour = () =>
        THEMES[document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark']

      /* Copy hold zone, in the art box's own pixel space. See the note above.
         The selectors are this band's runs of copy; .fw-chip covers the six
         framework pills, whose row is the widest thing here. */
      let hold = null
      const measureHold = (rect) => {
        const els = page.querySelectorAll(
          '#frameworks .t-eyebrow, #frameworks .fw-chip, #frameworks .t-body')
        let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity
        for (const el of els) {
          const r = el.getBoundingClientRect()
          if (!r.width || !r.height) continue
          x1 = Math.min(x1, r.left); y1 = Math.min(y1, r.top)
          x2 = Math.max(x2, r.right); y2 = Math.max(y2, r.bottom)
        }
        if (!isFinite(x1)) { hold = null; return }
        const cx = (x1 + x2) / 2 - rect.left
        const cy = (y1 + y2) / 2 - rect.top
        hold = {
          cx, cy,
          rx: Math.max(1, (x2 - x1) / 2 * HOLD_PAD),
          ry: Math.max(1, (y2 - y1) / 2 * HOLD_PAD),
        }
      }

      const resize = () => {
        const r = art.getBoundingClientRect()
        if (!r.width || !r.height) return false
        dpr = Math.min(window.devicePixelRatio || 1, 2)
        W = Math.round(r.width)
        H = Math.round(r.height)
        canvas.width = Math.round(W * dpr)
        canvas.height = Math.round(H * dpr)
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        measureHold(r)
        return true
      }

      const draw = () => {
        if (!W || !H) return
        ctx.clearRect(0, 0, W, H)
        const { r, g, b } = themeColour()

        // Bucket the dots by quantised alpha so fillStyle is set ~ALPHA_STEPS
        // times per frame instead of once per dot. See the note above.
        const buckets = Array.from({ length: ALPHA_STEPS }, () => [])

        for (let row = 0; row < ROWS; row++) {
          const t = row / (ROWS - 1)                       // 0 = top/near, 1 = back
          // Rows stack downward, crowded toward the top like the asset's.
          const yOffset = Math.pow(t, ROW_CROWD) * SHEET_DEPTH * H
          const size = DOT_NEAR + (DOT_FAR - DOT_NEAR) * t
          const rowAlpha = ALPHA_NEAR + (ALPHA_FAR - ALPHA_NEAR) * t
          // Each row runs a little further through the wave than the one above,
          // which is what tilts the crest into a diagonal.
          const rowPhase = phase + t * DEPTH_PHASE

          for (let col = 0; col < COLS; col++) {
            const u = col / (COLS - 1)                     // 0..1 across
            const x = u * W
            const y = H * BASE + yOffset
              - Math.sin(u * WAVES * Math.PI * 2 + rowPhase) * H * AMPLITUDE

            if (y < -4 || y > H + 4) continue

            /* Elliptical hold over the measured copy box. d is the normalised
               radius: 0 dead centre, 1 on the ellipse, >1 outside and untouched.
               smoothstep on the way out so there is no visible rim. */
            let k = 1
            if (hold) {
              const dx = (x - hold.cx) / hold.rx
              const dy = (y - hold.cy) / hold.ry
              const d = Math.sqrt(dx * dx + dy * dy)
              if (d < 1) {
                const s = d * d * (3 - 2 * d)          // smoothstep(0..1)
                k = HOLD_MIN + (1 - HOLD_MIN) * s
              }
            }

            const a = rowAlpha * k
            const bucket = Math.min(ALPHA_STEPS - 1, Math.max(0, Math.round(a / ALPHA_NEAR * (ALPHA_STEPS - 1))))
            buckets[bucket].push(x, y, size)
          }
        }

        for (let i = 0; i < ALPHA_STEPS; i++) {
          const list = buckets[i]
          if (!list.length) continue
          ctx.fillStyle = `rgba(${r},${g},${b},${(i / (ALPHA_STEPS - 1) * ALPHA_NEAR).toFixed(3)})`
          for (let k = 0; k < list.length; k += 3) {
            const s = list[k + 2]
            ctx.fillRect(list[k] - s / 2, list[k + 1] - s / 2, s, s)
          }
        }
      }

      const frame = (now) => {
        // Delta-timed, not per-frame-incremented, so the wave travels at the
        // same speed on a 60Hz and a 144Hz display and does not jump after the
        // tab has been backgrounded.
        const dt = last ? Math.min((now - last) / 1000, 0.05) : 0
        last = now
        phase += dt * TRAVEL * Math.PI * 2
        draw()
        raf = requestAnimationFrame(frame)
      }

      const stop = () => { if (raf) { cancelAnimationFrame(raf); raf = 0 }; last = 0 }
      const start = () => {
        if (raf || reduce.matches) return
        last = 0
        raf = requestAnimationFrame(frame)
      }

      if (!resize()) { /* zero-size band (parked): the observers below re-run */ }
      draw()   // one frame immediately, so the band is never empty - and this is
               // the whole of the reduced-motion behaviour

      const io = new IntersectionObserver((entries) => {
        for (const e of entries) e.isIntersecting ? start() : stop()
      })
      io.observe(art)

      const ro = new ResizeObserver(() => { if (resize()) draw() })
      ro.observe(art)

      const onReduce = () => { stop(); if (!reduce.matches) start(); else draw() }
      reduce.addEventListener('change', onReduce)

      // Theme flips are an attribute on <html>; redraw so the dots recolour even
      // while the band is paused off-screen.
      const themeObs = new MutationObserver(() => draw())
      themeObs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })

      cleanups.current.push(() => {
        stop()
        io.disconnect()
        ro.disconnect()
        themeObs.disconnect()
        reduce.removeEventListener('change', onReduce)
        canvas.remove()
        delete art.dataset[BUILT]
      })
    }

    build()

    /* Kept for the session with per-element idempotency: home is parked on
       navigation and re-attached as the SAME node later, so a one-shot observer
       would animate the band on the first visit only. */
    observer = new MutationObserver(build)
    observer.observe(document.body, { childList: true, subtree: true })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-route'] })
    window.addEventListener('pb-pages-mounted', build)

    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('pb-pages-mounted', build)
      for (const c of cleanups.current) c()
      cleanups.current = []
    }
  }, { dependencies: [] })

  return null
}
